import React, { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const INTERESTS = [
  { value: "reiki_teaching", key: "interest_teaching" },
  { value: "reiki_embodiment", key: "interest_embodiment" },
  { value: "wellness_clinic", key: "interest_beauty" },
  { value: "curated_products", key: "interest_products" },
  { value: "other", key: "interest_other" },
];

export default function FutureInterest() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    preferred_language: lang,
    interest_type: "reiki_teaching",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/interest", form);
      toast.success(t("future_interest.success"));
      setForm({ ...form, notes: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="future-interest-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow">{t("future_interest.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("future_interest.title")}</h1>
        <p className="mt-6 text-forest-700 leading-relaxed">{t("future_interest.lede")}</p>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 grid md:grid-cols-2 gap-5" data-testid="interest-form">
          <div>
            <label className="field-label">{t("book.name")}</label>
            <input required data-testid="interest-name" className="field-input" value={form.name} onChange={onChange("name")} />
          </div>
          <div>
            <label className="field-label">{t("book.email")}</label>
            <input required type="email" data-testid="interest-email" className="field-input" value={form.email} onChange={onChange("email")} />
          </div>
          <div>
            <label className="field-label">{t("book.phone")}</label>
            <input data-testid="interest-phone" className="field-input" value={form.phone} onChange={onChange("phone")} />
          </div>
          <div>
            <label className="field-label">{t("book.lang")}</label>
            <select data-testid="interest-language" className="field-input" value={form.preferred_language} onChange={onChange("preferred_language")}>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
              <option value="uk">Українська</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Interest</label>
            <div className="grid md:grid-cols-2 gap-3">
              {INTERESTS.map((it) => (
                <label
                  key={it.value}
                  data-testid={`interest-option-${it.value}`}
                  className={`cursor-pointer rounded-2xl border p-4 text-sm transition ${
                    form.interest_type === it.value
                      ? "border-wheat-500 bg-wheat-50/60 text-forest-900"
                      : "border-forest-900/15 bg-white/60 text-forest-700 hover:border-wheat-500/50"
                  }`}
                >
                  <input type="radio" name="interest" className="sr-only" checked={form.interest_type === it.value} onChange={() => setForm({ ...form, interest_type: it.value })} />
                  {t(`future_interest.${it.key}`)}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="field-label">{t("book.notes")}</label>
            <textarea rows={4} data-testid="interest-notes" className="field-input" value={form.notes} onChange={onChange("notes")} />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="interest-submit">
              {submitting ? t("common.loading") : t("future_interest.submit")}
            </button>
            {!user && (
              <span className="ml-4 text-sm text-forest-700">
                <Link to="/register" className="underline underline-offset-4 hover:text-forest-900">{t("nav.register")}</Link>{" "}— {t("future_interest.points_note")}
              </span>
            )}
          </div>
        </form>

        <aside className="card-warm">
          <p className="eyebrow">Loyalty</p>
          <h3 className="mt-3 font-serif text-2xl text-forest-900">Early points</h3>
          <p className="mt-3 text-sm text-forest-700 leading-relaxed">{t("future_interest.points_note")}</p>
          {user ? (
            <p className="mt-6 text-sm text-forest-800"><strong className="font-serif text-2xl text-wheat-700">{user.points_total ?? 0}</strong> pts</p>
          ) : (
            <Link to="/register" className="btn-secondary mt-6" data-testid="interest-signup-link">{t("nav.register")}</Link>
          )}
        </aside>
      </div>
    </div>
  );
}
